/** TypeScript interfaces for engineering analysis results. */

// =========================================================================
// Capacity Analysis
// =========================================================================

export type LoadSource = 'model' | 'measured'

/** Basis actually used for a transformer's loading number (spec §3.4 hybrid
 *  convention): 'measured' (adequate coverage, every meter reporting),
 *  'hybrid' (adequate coverage but some silent meters — measured peak plus a
 *  flat avg-demand estimate per silent meter), or 'model' (no AMI / below
 *  coverage). */
export type LoadingBasis = 'model' | 'measured' | 'hybrid'

export interface TransformerLoadingResult {
  id: number
  name: string | null
  gis_id: string | null
  kva_rating: number | null
  phase: string | null
  feeder_id: number | null
  feeder_name: string | null
  meter_count: number
  estimated_kw: number
  loading_pct: number | null
  loading_status: 'normal' | 'heavy' | 'overloaded' | 'underloaded' | 'no_rating'
  lat: number | null
  lon: number | null
  /** Basis actually used for this transformer (always 'model' when model was requested or AMI data was missing/below coverage). */
  load_source: LoadSource
  /** AMI peak kW used when load_source === 'measured', else null. */
  measured_peak_kw: number | null
  /** Model estimate % — always computed (spec §3.4 side-by-side columns). */
  model_loading_pct: number | null
  /** Measured max % — present only when AMI passed the coverage gate. */
  measured_loading_pct: number | null
  /** Hybrid-basis overlay (additive; spec §3.4). 'measured' | 'hybrid' |
   *  'model'. When basis !== 'model', loading_pct/loading_status carry the
   *  effective (measured + est.) value. */
  loading_basis: LoadingBasis
  /** Silent meters folded into the effective peak (meter_count - reporting). */
  meters_missing: number
  /** missing × avg_demand_kw added on top of the measured coincident peak. */
  estimated_missing_kw: number
  /** peak_kw_max + estimated_missing_kw; null on the model basis. */
  effective_peak_kw: number | null
  /** Effective loading % from effective_peak_kw; null on the model basis. */
  effective_loading_pct: number | null
  /** classify_loading() of the RAW effective_loading_pct (server-computed, so
   *  the table and both map layers color from ONE classification instead of
   *  re-deriving it from the display-rounded number). null on the model basis /
   *  when effective_loading_pct is null. */
  effective_loading_status: 'normal' | 'heavy' | 'overloaded' | 'underloaded' | 'no_rating' | null
  /** A7 (spec §3.9): true when BOTH model_loading_pct and measured_loading_pct
   *  exist and disagree by more than capacity_divergence_pp (server-computed
   *  from the PURE percentages — never the hybrid effective value). */
  model_measured_divergent: boolean
}

export interface TransformerLoadingSummary {
  total: number
  overloaded: number
  heavy: number
  normal: number
  underloaded: number
  no_rating: number
  avg_loading_pct: number | null
  avg_demand_kw_used: number
  transformers: TransformerLoadingResult[]
  /** Echoes the requested load_source. */
  load_source_requested: LoadSource
  /** AMI as-of date (max TransformerLoadDaily.date); null for model basis. */
  as_of: string | null
  /** Transformers that used measured data. */
  measured_count: number
  /** Transformers that fell back to model. */
  model_fallback_count: number
  /** Window used; null for model basis. */
  window_days: number | null
}

export interface FeederLoadingSummary {
  feeder_id: number
  feeder_name: string | null
  substation_id: number | null
  substation_name: string | null
  transformer_count: number
  meter_count: number
  total_kva: number
  total_estimated_kw: number
  loading_pct: number | null
  overloaded_count: number
  heavy_count: number
  underloaded_count: number
  /** Basis provenance (spec §3.4); 'model' unless measured was requested. */
  load_source_requested: LoadSource
  as_of: string | null
  window_days: number | null
  measured_count: number
  model_fallback_count: number
}

export interface PhaseDetail {
  phase: string
  transformer_count: number
  total_kva: number
  /** Meters served, weighted: a transformer's meters are shared 1/len(phases)
   *  across the phases it carries — the same weighting total_kva and
   *  estimated_kw use, so one row is internally consistent. A float (e.g.
   *  3.3), not a headcount; render it as "N meters served", not "N meters". */
  meter_count: number
  estimated_kw: number
  pct_of_total: number
}

export interface PhaseBalanceResult {
  feeder_id: number
  feeder_name: string | null
  phases: PhaseDetail[]
  imbalance_pct: number
  /** null when phase identity is unknown for more than 10% of the feeder's
   *  transformers (count-encoded or NULL GIS PHASING). Render the third
   *  "Phase identity unknown" state — null is NOT "Imbalanced". */
  is_balanced: boolean | null
  phase_identity_unknown_count: number
}

// =========================================================================
// Voltage Drop
// =========================================================================

export interface SegmentVoltageDrop {
  segment_id: number
  gis_id: string | null
  from_node: string | null
  to_node: string | null
  length_ft: number
  r_ohm_per_mile: number | null
  x_ohm_per_mile: number | null
  current_amps: number
  voltage_drop_volts: number
  cumulative_drop_pct: number
  drop_status: 'normal' | 'caution' | 'violation'
  uses_default_impedance: boolean
}

export interface TransformerVoltageDrop {
  transformer_id: number
  name: string | null
  kva_rating: number | null
  meter_count: number
  path_length_ft: number
  cumulative_drop_pct: number
  /** null when the transformer could not be placed on the feeder's conductor
   *  graph (review E5) — render a dash plus the status chip, never 1.0. */
  estimated_voltage_pu: number | null
  drop_status: 'normal' | 'caution' | 'violation' | 'islanded' | 'unplaceable'
  /** 'unplaceable' = no conductor within 100 ft on either placement path; the
   *  row contributed no load to the analysis. */
  placement_status: 'placed' | 'unplaceable'
  /** False when the transformer's node is unreachable from the source
   *  (islanded; such rows carry estimated_voltage_pu=0.0), and for every
   *  unplaceable row. */
  source_reachable: boolean
}

export interface VoltageDropResult {
  feeder_id: number
  feeder_name: string | null
  nominal_voltage_kv: number | null
  total_segments: number
  segments_missing_impedance: number
  max_drop_pct: number
  violation_count: number
  caution_count: number
  /** Source-orientation diagnostics (backend has always returned these —
   *  spec §3.5 surfaces them). */
  source_node: string | null
  graph_basis: string
  connected_components: number
  reachable_transformers: number
  unreachable_transformers: number
  reachable_load_kw: number
  unreachable_load_kw: number
  unreachable_segments: number
  looped_segments: number
  /** Transformers no placement path could snap to a conductor within 100 ft
   *  (review E5). Their load is in NEITHER reachable_load_kw nor
   *  unreachable_load_kw — it never entered the model. */
  unplaceable_transformers: number
  transformer_drops: TransformerVoltageDrop[]
  segment_drops: SegmentVoltageDrop[]
  /** A3 (spec §3.5): modelling assumptions this run applied. */
  assumptions?: string[]
}

export interface VoltageDropSummaryItem {
  feeder_id: number
  feeder_name: string | null
  total_segments: number
  max_drop_pct: number
  violation_count: number
  caution_count: number
  /** Recommendation 2: echoes this feeder's VoltageDropResult.unplaceable_
   *  transformers, so unplaceable load is visible from the cross-feeder
   *  summary and not only from the per-feeder detail endpoint. Optional
   *  (additive) so existing consumers of this type stay unaffected. */
  unplaceable_transformers?: number
}

// =========================================================================
// What-If / New-Load Analysis (FR-ENG-204)
// =========================================================================

export interface WhatIfNewLoadRequest {
  transformer_id: number
  additional_kw: number
  avg_demand_kw?: number
  power_factor?: number
}

export interface TransformerLoadingImpact {
  transformer_id: number
  transformer_name: string | null
  kva_rating: number | null
  baseline_kw: number
  scenario_kw: number
  baseline_loading_pct: number | null
  scenario_loading_pct: number | null
  baseline_status: 'overloaded' | 'heavy' | 'normal' | 'underloaded' | 'no_rating'
  scenario_status: 'overloaded' | 'heavy' | 'normal' | 'underloaded' | 'no_rating'
}

export interface WhatIfNewLoadResult {
  feeder_id: number
  transformer_id: number
  additional_kw: number
  baseline: VoltageDropResult
  scenario: VoltageDropResult
  transformer_impact: TransformerLoadingImpact
}

// =========================================================================
// Loading Status Colors
// =========================================================================

export const LOADING_COLORS: Record<string, string> = {
  normal: '#2ecc71',
  heavy: '#f1c40f',
  overloaded: '#e74c3c',
  underloaded: '#3498db',
  no_rating: '#95a5a6',
}

export const VDROP_COLORS: Record<string, string> = {
  normal: '#2ecc71',
  caution: '#f1c40f',
  violation: '#e74c3c',
  islanded: '#6b7280',
  unplaceable: '#9ca3af',
  no_data: '#95a5a6',
}
